import { GoogleUser, GoogleAuthResponse } from '../types/auth';

// Declaração de tipos para a nova Google Identity Services API
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfiguration) => void;
          prompt: (callback?: (notification: PromptMomentNotification) => void) => void;
          renderButton: (parent: HTMLElement, options: GsiButtonConfiguration) => void;
          disableAutoSelect: () => void;
          storeCredential: (credential: { id: string; password: string }) => void;
          cancel: () => void;
          onGoogleLibraryLoad: () => void;
          revoke: (hint: string, callback: (done: RevocationResponse) => void) => void;
        };
        oauth2: {
          initTokenClient: (config: TokenClientConfig) => TokenClient;
          hasGrantedAnyScope: (tokenResponse: TokenResponse, ...scopes: string[]) => boolean;
          hasGrantedAllScopes: (tokenResponse: TokenResponse, ...scopes: string[]) => boolean;
          revoke: (accessToken: string, callback?: () => void) => void;
        };
      };
    };
  }
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (credentialResponse: CredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface CredentialResponse {
  credential: string;
  select_by: string;
}

interface GsiButtonConfiguration {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string;
  locale?: string;
}

interface TokenClientConfig {
  client_id: string;
  scope: string;
  callback: (tokenResponse: TokenResponse) => void;
  error_callback?: (error: any) => void;
}

interface TokenClient {
  error_callback: (error: any) => void;
  callback: (tokenResponse: TokenResponse) => Promise<void>;
  requestAccessToken: (overrideConfig?: Partial<TokenClientConfig>) => void;
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

interface PromptMomentNotification {
  isDisplayMoment: () => boolean;
  isDisplayed: () => boolean;
  isNotDisplayed: () => boolean;
  getNotDisplayedReason: () => string;
  isSkippedMoment: () => boolean;
  getSkippedReason: () => string;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => string;
  getMomentType: () => string;
}

interface RevocationResponse {
  successful: boolean;
  error?: string;
}

class GoogleAuthService {
  private isInitialized = false;
  private clientId: string;
  private tokenClient: TokenClient | null = null;

  constructor() {
    this.clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!this.clientId) {
      throw new Error('Google Client ID não configurado. Verifique a variável VITE_GOOGLE_CLIENT_ID');
    }

    // Aguarda o carregamento da biblioteca do Google
    await this.waitForGoogleLibrary();

    // Inicializa o cliente de token OAuth2
    this.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: this.clientId,
      scope: 'openid profile email',
      callback: () => {}, // Será sobrescrito em cada chamada
    });

    this.isInitialized = true;
  }

  private waitForGoogleLibrary(): Promise<void> {
    return new Promise((resolve, reject) => {
      const checkGoogle = () => {
        if (window.google && window.google.accounts) {
          resolve();
        } else {
          setTimeout(checkGoogle, 100);
        }
      };

      // Timeout após 10 segundos
      setTimeout(() => {
        reject(new Error('Timeout ao carregar Google Identity Services'));
      }, 10000);

      checkGoogle();
    });
  }

  async signIn(): Promise<GoogleAuthResponse> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.tokenClient) {
      throw new Error('Google Token Client não inicializado');
    }

    return new Promise((resolve, reject) => {
      // Configura o callback para esta requisição específica
      this.tokenClient!.callback = async (tokenResponse: TokenResponse) => {
        try {
          // Busca informações do perfil usando o access token
          const userInfo = await this.fetchUserProfile(tokenResponse.access_token);
          
          const user: GoogleUser = {
            id: userInfo.id,
            email: userInfo.email,
            name: userInfo.name,
            picture: userInfo.picture,
            given_name: userInfo.given_name,
            family_name: userInfo.family_name
          };

          resolve({
            access_token: tokenResponse.access_token,
            id_token: tokenResponse.access_token, // ✅ Usa access_token como id_token
            user
          });
        } catch (error) {
          reject(error);
        }
      };

      // Configura callback de erro
      this.tokenClient!.error_callback = (error: any) => {
        reject(new Error(`Erro no login com Google: ${error.error || 'Erro desconhecido'}`));
      };

      // Solicita o token
      this.tokenClient!.requestAccessToken();
    });
  }



  private async fetchUserProfile(accessToken: string): Promise<any> {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Erro ao buscar informações do perfil');
    }

    return response.json();
  }

  async signOut(): Promise<void> {
    // A nova API não tem um método de signOut centralizado
    // O logout é gerenciado pela aplicação removendo os tokens
  }

  isSignedIn(): boolean {
    // Na nova API, o estado de login é gerenciado pela aplicação
    return false;
  }

  getCurrentUser(): GoogleUser | null {
    // Na nova API, as informações do usuário são gerenciadas pela aplicação
    return null;
  }
}

export const googleAuthService = new GoogleAuthService();

export type { GoogleAuthResponse };
